import React, { useState, useEffect } from 'react';
import {
    Button,
    Form,
    Input,
    Col,
    Row,
    Drawer,
    message,
    Select,
    Descriptions,
    Spin,
    DatePicker
} from 'antd';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import * as adminactions from "store/actions/AuthActions";
import TextArea from 'antd/lib/input/TextArea';
import useIsMobile from 'hooks/useIsMobile';

// Dates travel as 'YYYY-MM-DD' strings but the DatePicker needs dayjs values
const DATE_FIELDS = ['birth_date', 'marriage_anniversary_date'];
const API_DATE_FORMAT = 'YYYY-MM-DD';
const DISPLAY_DATE_FORMAT = 'DD-MM-YYYY';

const toFormValues = (contact) => {
    const values = { ...(contact || {}) };
    DATE_FIELDS.forEach((field) => {
        values[field] = values[field] ? dayjs(values[field]) : null;
    });
    return values;
};

const formatDate = (value) => (value ? dayjs(value).format(DISPLAY_DATE_FORMAT) : '-');

// `initialData` pre-fills a new contact, e.g. { account, account_label } when
// adding from an account page.
const ContactDrawer = ({ visible, onClose, contactId, mode = 'edit', onSuccess, initialData }) => {
    const isMobile = useIsMobile();
    const [form] = Form.useForm();
    const [dataSource, setDataSource] = useState(null);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [accounts, setAccounts] = useState([]);
    const [vendors, setVendors] = useState([]);

    const country_options = useSelector(store => store.master?.countries);

    const isViewMode = mode === 'view';
    const isCreateMode = !contactId;

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (visible) {
            fetchAccounts();
            fetchVendors();
            if (contactId) {
                retrieveContact();
            } else {
                setDataSource(initialData || null);
                // resetFields() falls back to the previous initialValues, which
                // can be the contact edited last - blank every field explicitly.
                form.resetFields();
                const blank = Object.fromEntries(
                    Object.keys(form.getFieldsValue(true)).map((field) => [field, undefined])
                );
                form.setFieldsValue({ ...blank, ...toFormValues(initialData) });
            }
        }
    }, [visible, contactId]);

    const fetchAccounts = async () => {
        // loadAccounts returns the axios response, loadVendors returns its
        // .data — unwrap here or `accounts.results` is undefined and the
        // Select falls back to rendering the raw UUID.
        const response = await adminactions.loadAccounts(1, undefined, 1000);
        if (response?.data) {
            setAccounts(response.data);
        }
    };

    const fetchVendors = async () => {
        const response = await adminactions.loadVendors(1, undefined, 1000);
        if (response) {
            setVendors(response);
        }
    };

    const accountOptions = (() => {
        const options = (accounts?.results || []).map((account) => ({
            value: account.id,
            // "A202600001 - Acme Corp Pvt Ltd"
            label: [account.account_number, account.name].filter(Boolean).join(' - '),
        }));
        const current = dataSource?.account;
        if (current && !options.some((option) => option.value === current)) {
            options.unshift({
                value: current,
                label: dataSource?.account_label || dataSource?.account_name || current,
            });
        }
        return options;
    })();

    const vendorOptions = (() => {
        const options = (vendors?.results || []).map((vendor) => ({
            value: vendor.id,
            label: vendor.name,
        }));
        const current = dataSource?.vendor;
        if (current && !options.some((option) => option.value === current)) {
            options.unshift({ value: current, label: dataSource?.vendor_name || current });
        }
        return options;
    })();

    const retrieveContact = async () => {
        setLoading(true);
        try {
            const response = await adminactions.retrieveContact(contactId);
            if (response) {
                setDataSource(response);
                form.setFieldsValue(toFormValues(response));
            }
        } catch (error) {
            message.error('Failed to load contact details');
        }
        setLoading(false);
    };

    const onFinish = async (formValues) => {
        const values = { ...formValues };
        DATE_FIELDS.forEach((field) => {
            values[field] = values[field] ? dayjs(values[field]).format(API_DATE_FORMAT) : null;
        });
        setSubmitting(true);
        try {
            let response;
            if (contactId) {
                response = await adminactions.updateContacts(values, contactId);
                message.success('Contact updated successfully');
            } else {
                response = await adminactions.addContacts(values);
                message.success('Contact created successfully');
            }

            if (response) {
                form.resetFields();
                onSuccess && onSuccess();
                onClose();
            }
        } catch (error) {
            message.error(`Failed to ${contactId ? 'update' : 'create'} contact`);
        }
        setSubmitting(false);
    };

    const getTitle = () => {
        if (isViewMode) return 'View Contact';
        if (isCreateMode) return 'Create Contact';
        return 'Edit Contact';
    };

    const renderViewMode = () => {
        if (!dataSource) return null;

        return (
            <div style={{ padding: '8px 0' }}>
                <Descriptions
                    bordered
                    column={1}
                    size="small"
                    labelStyle={{ fontWeight: 600, width: '200px' }}
                >
                    {/* Personal Information */}
                    <Descriptions.Item label="First Name">
                        {dataSource.first_name || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Middle Name">
                        {dataSource.middle_name || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Last Name">
                        {dataSource.last_name || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Company">
                        {dataSource.company || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Job Title">
                        {dataSource.job_title || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Birth Date">
                        {formatDate(dataSource.birth_date)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Marriage Anniversary">
                        {formatDate(dataSource.marriage_anniversary_date)}
                    </Descriptions.Item>

                    {/* Contact Information */}
                    <Descriptions.Item label="Email">
                        {dataSource.email || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Alternate Email">
                        {dataSource.alt_email || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Phone Number">
                        {dataSource.phone_number || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Alternate Phone">
                        {dataSource.alt_phone_number || '-'}
                    </Descriptions.Item>

                    {/* Relationships */}
                    <Descriptions.Item label="Account">
                        {dataSource.account_name || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Vendor">
                        {dataSource.vendor_name || '-'}
                    </Descriptions.Item>

                    {/* Address Information */}
                    <Descriptions.Item label="Address">
                        {dataSource.address || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="City">
                        {dataSource.city || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="State">
                        {dataSource.state || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Pincode">
                        {dataSource.pincode || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Country">
                        {dataSource.country || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Website">
                        {dataSource.website || '-'}
                    </Descriptions.Item>

                    {/* Additional Information */}
                    <Descriptions.Item label="Notes">
                        {dataSource.notes || '-'}
                    </Descriptions.Item>
                </Descriptions>
            </div>
        );
    };

    const renderFormMode = () => {
        return (
            <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                initialValues={toFormValues(dataSource)}
            >
                <Row gutter={[16, 0]}>
                    {/* Personal Information */}
                    <Col span={24}>
                        <h3 style={{
                            fontSize: '14px',
                            fontWeight: 600,
                            marginBottom: '12px',
                            marginTop: '12px',
                            color: '#1890ff'
                        }}>
                            Personal Information
                        </h3>
                    </Col>

                    <Col span={12}>
                        <Form.Item label="First Name" name='first_name'>
                            <Input placeholder="Enter first name" />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item label="Middle Name" name='middle_name'>
                            <Input placeholder="Enter middle name" />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item label="Last Name" name='last_name'>
                            <Input placeholder="Enter last name" />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item label="Company" name='company'>
                            <Input placeholder="Enter company name" />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item label="Job Title" name='job_title'>
                            <Input placeholder="Enter job title" />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item label="Birth Date" name='birth_date'>
                            <DatePicker format={DISPLAY_DATE_FORMAT} style={{ width: '100%' }} placeholder="Select birth date" />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item label="Marriage Anniversary" name='marriage_anniversary_date'>
                            <DatePicker format={DISPLAY_DATE_FORMAT} style={{ width: '100%' }} placeholder="Select anniversary date" />
                        </Form.Item>
                    </Col>

                    {/* Contact Information */}
                    <Col span={24}>
                        <h3 style={{
                            fontSize: '14px',
                            fontWeight: 600,
                            marginBottom: '12px',
                            marginTop: '12px',
                            color: '#1890ff'
                        }}>
                            Contact Information
                        </h3>
                    </Col>

                    <Col span={12}>
                        <Form.Item label="Email" name='email'>
                            <Input type="email" placeholder="Enter email address" />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item label="Alternate Email" name='alt_email'>
                            <Input type="email" placeholder="Enter alternate email" />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item label="Phone Number" name='phone_number'>
                            <Input placeholder="Enter phone number" />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item label="Alternate Phone" name='alt_phone_number'>
                            <Input placeholder="Enter alternate phone" />
                        </Form.Item>
                    </Col>

                    {/* Relationships */}
                    <Col span={24}>
                        <h3 style={{
                            fontSize: '14px',
                            fontWeight: 600,
                            marginBottom: '12px',
                            marginTop: '12px',
                            color: '#1890ff'
                        }}>
                            Related Accounts & Vendors
                        </h3>
                    </Col>

                    <Col span={12}>
                        <Form.Item label="Account" name='account'>
                            <Select
                                showSearch
                                placeholder="Select an account"
                                allowClear
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                options={accountOptions}
                            />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item label="Vendor" name='vendor'>
                            <Select
                                showSearch
                                placeholder="Select a vendor"
                                allowClear
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                options={vendorOptions}
                            />
                        </Form.Item>
                    </Col>

                    {/* Address Information */}
                    <Col span={24}>
                        <h3 style={{
                            fontSize: '14px',
                            fontWeight: 600,
                            marginBottom: '12px',
                            marginTop: '12px',
                            color: '#1890ff'
                        }}>
                            Address Information
                        </h3>
                    </Col>

                    <Col span={24}>
                        <Form.Item label="Address" name="address">
                            <TextArea rows={2} placeholder="Enter complete address" />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item label="City" name="city">
                            <Input placeholder="Enter city" />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item label="State" name="state">
                            <Input placeholder="Enter state" />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item label="Pincode" name="pincode">
                            <Input placeholder="Enter pincode" />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item label="Country" name="country">
                            <Select
                                showSearch
                                placeholder="Search to Select"
                                optionFilterProp="children"
                                filterOption={(input, option) => (option?.label?.toLowerCase?.() ?? '').includes(input?.toLowerCase?.())}
                                filterSort={(optionA, optionB) =>
                                    (optionA?.label ?? '').toLowerCase().localeCompare((optionB?.label ?? '').toLowerCase())
                                }
                                loading={Array.isArray(country_options) && country_options?.length > 0 ? false : true}
                                options={country_options?.map?.((opt, ind) => {
                                    return {
                                        label: `${opt.name}`,
                                        value: opt.id
                                    }
                                }) || []}
                            />
                        </Form.Item>
                    </Col>

                    <Col span={24}>
                        <Form.Item label="Website" name="website">
                            <Input placeholder="Enter website URL" />
                        </Form.Item>
                    </Col>

                    {/* Additional Information */}
                    <Col span={24}>
                        <h3 style={{
                            fontSize: '14px',
                            fontWeight: 600,
                            marginBottom: '12px',
                            marginTop: '12px',
                            color: '#1890ff'
                        }}>
                            Additional Notes
                        </h3>
                    </Col>

                    <Col span={24}>
                        <Form.Item label="Notes" name="notes">
                            <TextArea rows={3} placeholder="Enter any additional notes or comments" />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        );
    };

    return (
        <Drawer
            title={getTitle()}
            placement={isMobile ? "bottom" : "right"}
            width={isMobile ? "100%" : 720}
            height={isMobile ? "90vh" : undefined}
            onClose={onClose}
            open={visible}
            styles={{
                body: {
                    paddingBottom: isMobile ? 80 : 24
                }
            }}
            footer={
                !isViewMode && (
                    <div style={{ display: 'flex', justifyContent: isMobile ? 'stretch' : 'flex-end', gap: '8px' }}>
                        <Button onClick={onClose} style={isMobile ? { flex: 1 } : undefined}>Cancel</Button>
                        <Button
                            type="primary"
                            onClick={() => form.submit()}
                            loading={submitting}
                            style={isMobile ? { flex: 1 } : undefined}
                        >
                            {isCreateMode ? 'Create Contact' : 'Update Contact'}
                        </Button>
                    </div>
                )
            }
        >
            <Spin spinning={loading}>
                {isViewMode ? renderViewMode() : renderFormMode()}
            </Spin>
        </Drawer>
    );
};

export default ContactDrawer;
